import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Card, CardBody, CardHeader, Col, Container, Row, Spinner, Label, Input, Button } from 'reactstrap'
import TableContainer from '../../Components/Common/TableContainerReactTable';
import useObtenerVehiculosPorTenant from '../../Components/Hooks/useObtenerVehiculosPorTenant';
import useCreateVehiculo from '../../Components/Hooks/useCreateVehiculo';
import ModalCrearVehiculo from '../../Components/modals/ModalCrearVehiculo';
import ModalLeerVehiculo from '../../Components/modals/ModalLeerVehiculo';
import ModalModificarVehiculo from '../../Components/modals/ModalModificarVehiculo';
import {
    getLegacyMarcas,
    getLegacyModelos,
    getLegacyColores,
    getLegacyTiposCombustible,
    getLegacyTiposTraccion,
    getLegacyTiposTransmision
} from '../../slices/thunks';

const Vehiculos = () => {

    const [limit, setLimit] = useState(100);
    const [selectedVehiculo, setSelectedVehiculo] = useState('');
    const [refresh, setRefresh] = useState(0);
    const { vehiculos, loading, error } = useObtenerVehiculosPorTenant(limit, refresh);
    const [modalCrearOpen, setModalCrearOpen] = useState(false);
    const [modalLeerOpen, setModalLeerOpen] = useState(false);
    const [modalModificarOpen, setModalModificarOpen] = useState(false);
    const { createVehiculo, loading: createLoading, error: createError } = useCreateVehiculo();

    const tenantId = useSelector((state) => state.Context.tenantId);
    const empresa = useSelector((state) => state.Context.empresa);
    const marcas = useSelector((state) => state.LegacyMarcas.legacyMarcas);
    const modelos = useSelector((state) => state.LegacyModelos.legacyModelos);
    const colores = useSelector((state) => state.LegacyColores.legacyColores);
    const tiposCombustible = useSelector((state) => state.LegacyTiposCombustible.legacyTiposCombustible);
    const tiposTraccion = useSelector((state) => state.LegacyTiposTraccion.legacyTiposTraccion);
    const tiposTransmision = useSelector((state) => state.LegacyTiposTransmision.legacyTiposTransmision);
    const dispatch = useDispatch();

    useEffect(() => {
        document.title = "Vehículos | Siga ERP";
    }, []);

    useEffect(() => {
        dispatch(getLegacyMarcas({ tenantId, empresa }));
        dispatch(getLegacyColores({ tenantId, empresa }));
        dispatch(getLegacyTiposCombustible({ tenantId, empresa }));
        dispatch(getLegacyTiposTraccion({ tenantId, empresa }));
        dispatch(getLegacyTiposTransmision({ tenantId, empresa }));
    }, [tenantId, empresa, dispatch]);

    function buscarNombreMarca(codigo) {
        if (Array.isArray(marcas) && marcas.length > 0)
            return marcas?.find(m => m.MARCAS == codigo)?.DESCRI || codigo;
    }

    function buscarNombreModelo(codigo) {
        if (Array.isArray(modelos) && modelos.length > 0)
            return modelos?.find(m => m.MODELO == codigo)?.DESCRI || codigo;
    }

    function buscarColor(codigo) {
        if (Array.isArray(colores) && colores.length > 0)
            return colores?.find(m => m.codreg == codigo)?.descri || codigo;
    }

    function buscarTipoCombustible(codigo) {
        if (Array.isArray(tiposCombustible) && tiposCombustible.length > 0)
            return tiposCombustible?.find(m => m.codreg == codigo)?.descri || codigo;
    }

    function buscarTipoTraccion(codigo) {
        if (Array.isArray(tiposTraccion) && tiposTraccion.length > 0)
            return tiposTraccion?.find(m => m.codreg == codigo)?.descri || codigo;
    }

    function buscarTipoTransmision(codigo) {
        if (Array.isArray(tiposTransmision) && tiposTransmision.length > 0)
            return tiposTransmision?.find(m => m.codreg == codigo)?.descri || codigo;
    }

    const columns = useMemo(
        () => [
            {
                header: "Patente",
                accessorKey: "PATENT",
                enableColumnFilter: false,
            },
            {
                header: "Propietario",
                accessorKey: "CLIENT",
                enableColumnFilter: false,
            },
            {
                header: "Marca",
                accessorKey: "MARCAS",
                enableColumnFilter: false,
                cell: (info) => buscarNombreMarca(info.getValue()),
            },
            {
                header: "Modelo",
                accessorKey: "MODELO",
                enableColumnFilter: false,
                cell: (info) => buscarNombreModelo(info.getValue()),
            },
            {
                header: "Color",
                accessorKey: "CCOLOR",
                enableColumnFilter: false,
                cell: (info) => buscarColor(info.getValue()),
            },
            {
                header: "Año",
                accessorKey: "ANOFAB",
                enableColumnFilter: false,
            },
            {
                header: "VIN",
                accessorKey: "NROVIN",
                enableColumnFilter: false,
            },
            {
                header: "Chasis",
                accessorKey: "NCHASI",
                enableColumnFilter: false,
            },
            {
                header: "Motor",
                accessorKey: "NMOTOR",
                enableColumnFilter: false,
            },
            {
                header: "Transmisión",
                accessorKey: "TRASMI",
                enableColumnFilter: false,
                cell: (info) => buscarTipoTransmision(info.getValue()),
            },
            {
                header: "Tracción",
                accessorKey: "TRACCI",
                enableColumnFilter: false,
                cell: (info) => buscarTipoTraccion(info.getValue()),
            },
            {
                header: "Combustible",
                accessorKey: "COMBUS",
                enableColumnFilter: false,
                cell: (info) => buscarTipoCombustible(info.getValue()),
            },

        ],
        [marcas, modelos, colores, tiposCombustible, tiposTraccion, tiposTransmision]
    );

    const handleCellClick = (cellInfo) => {
        console.log('Celda clickeada:', cellInfo);
        console.log('Columna:', cellInfo.columnId);
        console.log('Valor:', cellInfo.value);
        console.log('Datos completos de la fila:', cellInfo.rowData);
        setSelectedVehiculo(cellInfo.rowData.PATENT);
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader className="d-flex justify-content-between align-items-center">
                                    <h5 className="card-title mb-0">Vehículos</h5>
                                    <div className="mt-0">
                                        <Input
                                            type="text"
                                            value={selectedVehiculo}
                                            onChange={(e) => setSelectedVehiculo(e.target.value)}
                                            style={{ width: '250px' }}
                                        />
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <Button color="info" className="me-2" onClick={() => { if (selectedVehiculo) setModalLeerOpen(true); }} disabled={!selectedVehiculo}>
                                            <i className="ri-eye-line me-1"></i>Leer
                                        </Button>
                                        <Button color="warning" className="me-2" onClick={() => { if (selectedVehiculo) setModalModificarOpen(true) }} disabled={!selectedVehiculo}>
                                            <i className="ri-edit-line me-1"></i>Modificar
                                        </Button>
                                        <Button color="primary" className="me-2" onClick={() => setModalCrearOpen(true)} disabled={createLoading}>
                                            <i className="ri-add-line me-1"></i>Crear
                                        </Button>
                                        <Label className="form-label mb-0 me-2">Límite</Label>
                                        <div className="mt-0">
                                            <Input
                                                type="number"
                                                min={1}
                                                value={limit}
                                                onChange={(e) => setLimit(e.target.value)}
                                                style={{ width: '100px' }}
                                            />
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardBody>
                                    {error ? <div className="alert alert-danger">{error}</div> : loading ? <div className="text-center"><Spinner animation="border" variant="primary" /></div> : <TableContainer
                                        columns={(columns || [])}
                                        data={vehiculos || []}
                                        customPageSize={15}
                                        isGlobalFilter={true}
                                        SearchPlaceholder='Buscar...'
                                        onCellClick={handleCellClick}
                                    />}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
            <ModalCrearVehiculo
                isOpen={modalCrearOpen}
                toggle={() => setModalCrearOpen(false)}
                onSave={async (formData) => {
                    const transformedData = {
                        CLAEMP: formData.empresa,
                        PATENT: formData.patente,
                        MARCAS: formData.marca.value,
                        MODELO: formData.modelo.value,
                        NROVIN: formData.vin,
                        NCHASI: formData.chasis,
                        CCOLOR: formData.color.value,
                        COMBUS: formData.tipoCombustible.value,
                        TRACCI: formData.tipoTraccion.value,
                        TRASMI: formData.tipoTransmision.value,
                        ANOFAB: formData.ano,
                        NMOTOR: formData.motor,
                        CLIENT: formData.rut
                    };
                    const result = await createVehiculo(transformedData);
                    console.log('Resultado de la creación del vehículo:', result);
                    if (result.success) {
                        alert('Vehículo creado exitosamente');
                        setModalCrearOpen(false);
                        setRefresh(prev => prev + 1); // Refresh the table
                    } else {
                        alert('Error al crear vehículo: ' + result.error);
                    }
                }}
            />
            <ModalLeerVehiculo
                isOpen={modalLeerOpen}
                toggle={() => setModalLeerOpen(false)}
                selectedVehiculo={selectedVehiculo}
            />
            <ModalModificarVehiculo
                isOpen={modalModificarOpen}
                toggle={() => setModalModificarOpen(false)}
                onSave={() => {
                    setRefresh(prev => prev + 1); // Refresh the table
                }}
                selectedVehiculo={selectedVehiculo}
            />
        </React.Fragment>
    )
}

export default Vehiculos;